import { FC } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import { styled } from '@mui/material/styles';
import { Control, Controller, UseFormGetValues } from 'react-hook-form';
import { Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import DatePicker from 'further-ui/components/DatePicker';
import { CalendarDay } from 'further-ui/utils';
import BlockNoteEditor from 'further-ui/components/BlockNoteEditor';
import { type RegisterFlushBlockNoteDocument } from 'further-ui/components';
import { useNotification } from 'hooks/ui/useNotification';
import { PeriodEndReportFormValues } from './schema';

const OuterColumn = styled(Column)({
  maxWidth: '800px',
});

const InnerColumn = styled(Column)({
  maxWidth: '400px',
});

type Props = {
  control: Control<PeriodEndReportFormValues>;
  readOnly: boolean;
  getValues: UseFormGetValues<PeriodEndReportFormValues>;
  periodStart: CalendarDay;
  periodEnd: CalendarDay;
  registerCommentaryFlush: RegisterFlushBlockNoteDocument;
};

const GeneralInformation: FC<Props> = ({
  control,
  readOnly,
  getValues,
  periodStart,
  periodEnd,
  registerCommentaryFlush,
}) => {
  const { error } = useNotification();

  const commentary = getValues('commentary');
  const hasCommentary = Array.isArray(commentary) && commentary.length > 0;

  return (
    <Column spacing="lg">
      <OuterColumn spacing={readOnly ? 'sm' : 'lg'}>
        <Typography variant="h5" color="grey.900">
          General information
        </Typography>
        <InnerColumn spacing="lg">
          <FormField
            label="Report name"
            labelColorLight={readOnly}
            spacing={readOnly ? 'xs' : 'sm'}
          >
            <Controller
              control={control}
              name="title"
              render={({ field, fieldState }) =>
                readOnly ? (
                  <Typography variant="h6">{field.value}</Typography>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter report name"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )
              }
            />
          </FormField>
          <FormField
            label="Report publication date"
            labelColorLight={readOnly}
            spacing={readOnly ? 'xs' : 'sm'}
          >
            <Controller
              control={control}
              name="reportDate"
              render={({ field, fieldState }) =>
                readOnly ? (
                  <Typography variant="h6">
                    {field.value?.toReadable()}
                  </Typography>
                ) : (
                  <DatePicker
                    fullWidth
                    size="small"
                    placeholder="Enter report publication date"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )
              }
            />
          </FormField>
        </InnerColumn>
      </OuterColumn>
      {readOnly ? (
        <>
          <Column spacing="xs">
            <Typography variant="h6" color="grey.600">
              Reporting period
            </Typography>
            <Typography variant="h6">{`${periodStart.toReadable()} – ${periodEnd.toReadable()}`}</Typography>
          </Column>
          {hasCommentary ? <Divider /> : null}
        </>
      ) : null}
      {!readOnly || hasCommentary ? (
        <OuterColumn spacing="sm" fullWidth>
          <Typography variant={readOnly ? 'h5' : 'h6'} color="grey.900">
            Manager's fund commentary
          </Typography>
          <Column spacing="xs">
            {readOnly ? (
              <Typography variant="h6" color="grey.600">
                Details
              </Typography>
            ) : null}
            <Controller
              control={control}
              name="commentary"
              render={({ field, fieldState }) => (
                <Column spacing="xs">
                  <BlockNoteEditor
                    value={field.value}
                    extraLines={1}
                    readOnly={readOnly}
                    onChange={readOnly ? undefined : field.onChange}
                    onError={readOnly ? undefined : error}
                    onRegisterFlush={registerCommentaryFlush}
                    placeholder={
                      readOnly
                        ? undefined
                        : 'This is the introduction to your report, where you can introduce the update and discuss relevant topics. When drafting, if in doubt about formatting, download a preview PDF using the ‘Download preview PDF’ button below. If left blank, the commentary section will not display in your report.'
                    }
                  />
                  {!readOnly && fieldState.error ? (
                    <FormHelperText error>
                      {fieldState.error.message}
                    </FormHelperText>
                  ) : null}
                </Column>
              )}
            />
          </Column>
        </OuterColumn>
      ) : null}
    </Column>
  );
};

export default GeneralInformation;
